import React, { useState, useEffect, useRef } from 'react';
import NotificationApi from '../services/notificationApi';
import SocketManager from '../utils/socketManager';
import SubUserApi from '../services/subUserApi';
import ConfirmModal from '../components/ConfirmModal';
import {getSubUser , getStatusScan , saveStatusScan } from '../utils/auth';

// Top-level mode icons array
const modes = [
  '/images/qr_mic.png',   // Mode 0
  '/images/mic.png',      // Mode 1
  '/images/qr_bell.png',  // Mode 2
  '/images/bell.png'      // Mode 3
];

// --- Helper functions ---

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleTimeString('en-GB');
};

const formatAmount = (amount) => {
    if (amount == null) return '-';
    if (amount % 1 === 0) {
      return new Intl.NumberFormat('en-US').format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
};

// --- Refactored Sub-component with Tailwind CSS ---
const ResponsiveGroupedNotificationTable = ({ notifications, limit = 20 }) => {
    // ... (logic for grouping and sorting remains the same) ...
    const grouped = notifications.reduce((acc, notif) => {
        const dateKey = formatDate(notif.createdDate);
        if (dateKey) {
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push(notif);
        }
        return acc;
      }, {});
    
      const sortedDates = Object.keys(grouped).sort((a, b) => {
        const [dayA, monthA, yearA] = a.split('/');
        const [dayB, monthB, yearB] = b.split('/');
        const dateA = new Date(yearA, monthA - 1, dayA);
        const dateB = new Date(yearB, monthB - 1, dayB);
        return dateB - dateA;
      });
    
      const items = [];
      let totalRowsAdded = 0;
      sortedDates.forEach((date) => {
        if (!date) return;
        items.push({ type: 'header', date });
        const notifList = grouped[date].sort(
          (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
        );
        const rowsToAdd = notifList.slice(0, limit - totalRowsAdded);
        rowsToAdd.forEach((notif) => items.push({ type: 'row', notification: notif }));
        totalRowsAdded += rowsToAdd.length;
        if (totalRowsAdded >= limit) return;
      });
  
    return (
      <div className="overflow-x-auto w-full text-white">
        <table className="w-full border-collapse">
          <tbody>
            {items.map((item, index) => {
              if (item.type === 'header') {
                return (
                  <tr key={`header-${index}`}>
                    <td colSpan="4" className="p-2 font-bold text-left">
                      {item.date}
                    </td>
                  </tr>
                );
              } else if (item.type === 'row') {
                const notif = item.notification;
                return (
                  <tr key={`row-${index}`}>
                    <td className="p-2 text-center">{notif.createdDate ? formatTime(notif.createdDate) : '-'}</td>
                    <td className="p-2 text-center">{notif.accountNumberTo || '-'}</td>
                    <td className="p-2 text-center">{notif.bankShortTo || '-'}</td>
                    <td className="p-2 text-right">{notif.amount != null ? formatAmount(notif.amount) : '-'}</td>
                  </tr>
                );
              }
              return null;
            })}
          </tbody>
        </table>
      </div>
    );
};

// วางไว้นอกคอมโพเนนต์ Notification
const calculateDaysRemaining = (endDateStr, currentDateStr) => {
  if (!endDateStr || !currentDateStr) return 0;

  try {
    const endDate = new Date(endDateStr);
    const currentDate = new Date(currentDateStr);
    
    // ตั้งค่าเวลาเป็น 00:00:00 เพื่อให้การคำนวณแม่นยำ ไม่นับรวมเวลา
    endDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const diffInMillis = endDate.getTime() - currentDate.getTime();
    const days = Math.ceil(diffInMillis / (1000 * 60 * 60 * 24));
    return days;
  } catch (e) {
    console.error("Error calculating days remaining:", e);
    return 0; // คืนค่า 0 หากมีข้อผิดพลาด
  }
};


// --- Main Notification Component (Refactored with Tailwind CSS) ---
const Notification = () => {
    // --- States ---
    const [subUser, setSubUser] = useState(getSubUser() || {});
    const [notifications, setNotifications] = useState([]);
    const [latestAmount, setLatestAmount] = useState("0.00");
    const [loading, setLoading] = useState(true);
    const [currentModeIndex, setCurrentModeIndex] = useState(parseInt(localStorage.getItem('currentMode')) || 0);
    const [time, setTime] = useState(new Date());
    const [daysRemain, setDaysRemain] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine); // ++ State เช็คสถานะ Internet

    // States สำหรับ UI
    const [accountNumberTo, setAccountNumberTo] = useState("-");
    const [bankTo, setBankTo] = useState("-");
    const [bankShortTo, setBankShortTo] = useState("-");
    const [createdDate, setCreatedDate] = useState("DD/MM/YYYY");
    const [createdTime, setCreatedTime] = useState("HH:mm:ss");
    const [bgColor, setBgColor] = useState('#969696');
    const [bdColor, setBdColor] = useState('#000000');

    // States สำหรับ Modal
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [showCancelButton, setShowCancelButton] = useState(true);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [onConfirmCallback, setOnConfirmCallback] = useState(() => {});
    const [countdown, setCountdown] = useState(null); // ++ State สำหรับนับถอยหลังใน Modal

    // States สำหรับ TTS
    const ttsRef = useRef(window.speechSynthesis);
    const [isTtsInitialized, setIsTtsInitialized] = useState(false);
    const [voices, setVoices] = useState([]);
    const countdownIntervalRef = useRef(null);
    const [isTtsSupported, setIsTtsSupported] = useState(false);


    const showConfirmation = (message, onConfirm, hasCountdown = false, countdownTime = 10,showCancelButton = true) => {
        clearInterval(countdownIntervalRef.current); // Clear previous interval
        setConfirmMessage(message);
        setOnConfirmCallback(() => () => { // Wrap onConfirm to clear interval
            clearInterval(countdownIntervalRef.current);
            onConfirm();
        });

        if (hasCountdown) {
            setCountdown(countdownTime);
            countdownIntervalRef.current = setInterval(() => {
                setCountdown(prevCountdown => {
                    if (prevCountdown <= 1) {
                        clearInterval(countdownIntervalRef.current);
                        onConfirm(); // Auto-confirm on timeout
                        return null;
                    }
                    return prevCountdown - 1;
                });
            }, 1000);
        } else {
            setCountdown(null);
        }
        setShowCancelButton(showCancelButton);
        setIsConfirmOpen(true);
    };
    
    const logoutUser = async (redirect = true) => {
        try {
            await SubUserApi.logout(subUser.id);
            localStorage.clear();
            if (redirect) {
                window.location.href = '/login';
            }
        } catch (error) {
            console.error("Error logging out:", error);
            // Even if API fails, clear local storage and redirect
            localStorage.clear();
            if (redirect) {
                window.location.href = '/login';
            }
        }
    };
    
    // ++ ฟังก์ชันสำหรับ Dialog ต่างๆ ที่เพิ่มเข้ามา
    const showKickLogoutDialog = () => {
        const onConfirm = () => {
            setIsConfirmOpen(false);
            logoutUser();
        };
        showConfirmation(
            "บัญชีของคุณกำลังเข้าสู่ระบบจากอุปกรณ์อื่น คุณจะถูกออกจากระบบโดยอัตโนมัติ",
            onConfirm,
            true, // Enable countdown
            10    // 10 seconds
        );
    };

    const showUserExpireDialog = () => {
        const onConfirm = () => {
            setIsConfirmOpen(false);
            logoutUser();
        };
        showConfirmation(
            "บัญชีของคุณหมดอายุการใช้งานแล้ว",
            onConfirm,
            true, // Enable countdown
            10    // 10 seconds
        );
    };

    const logoutConfirm = () => showConfirmation('ต้องการออกจากระบบใช่หรือไม่', () => {
        setIsConfirmOpen(false);
        logoutUser();
    });

    const fetchNotifications = async () => {
        try {
            const response = await NotificationApi.all(subUser.userId, subUser.id);
            if (response.data && response.data.length > 0) {
                const latest = response.data[0];
                setLatestAmount(Number(latest.amount).toFixed(2));
                setNotifications(response.data.slice(1));
                if (latest.bankColorTo) {
                    setBgColor(latest.bankColorTo.bgColor);
                    setBdColor(latest.bankColorTo.bdColor);
                }
                setAccountNumberTo(latest.accountNumberTo || "-");
                setBankTo(latest.bankTo || "-");
                setBankShortTo(latest.bankShortTo || "-");
                if (latest.createdDate) {
                    const d = new Date(latest.createdDate);
                    setCreatedDate(d.toLocaleDateString('en-GB'));
                    setCreatedTime(d.toLocaleTimeString('en-GB'));
                }
            } else {
                setNotifications([]);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    // ++ 1. useEffect สำหรับเช็คสถานะ Network ++
    useEffect(() => {
        const handleOnline = () => {
            console.log('Network is online.');
            setIsOnline(true);
            fetchNotifications();
        };
        const handleOffline = () => {
            console.log('Network is offline.');
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [notifications]);

    // ++ Event สำหรับอัปเดตสถานะ User หลัก
    const handleUserStatus = (data) => {
      if (data.userId === subUser.userId) {
          console.log("Real-time user status update:", data.status);
          setSubUser(prevSubUser => ({ ...prevSubUser, userStatus: data.status }));
      }
    };

    useEffect(() => {
      const checkUserStatusOnLoad = async () => {
          if (subUser && subUser.id) {
              try {
                  const response = await SubUserApi.checkUserStatus(subUser.id);
                  handleUserStatus({userId:subUser.userId,status:response.data})
              } catch (error) {
                  console.error("Failed to check user status:", error);
              }
          }
      };
      checkUserStatusOnLoad();
    }, [subUser.id]);

    // ++ 2. useEffect สำหรับเช็ควันหมดอายุของ User เมื่อโหลดหน้า ++
    useEffect(() => {
        const checkStatusOnLoad = async () => {
            if (subUser && subUser.username) {
                try {
                    const response = await SubUserApi.checkUsername(subUser.username);
                    const data = response.data;

                    if (data.status === 200 && data.data) {
                        const { active, remainingDays } = data.data;

                        setDaysRemain(remainingDays);

                        if(!active || remainingDays <=0){
                          showUserExpireDialog();
                        }
                    }
                } catch (error) {
                    console.error("Failed to check user status:", error);
                }
            }
        };
        checkStatusOnLoad();
    }, [subUser.username]); // ทำงานเมื่อ username พร้อมใช้งาน


    // ++ 3. useEffect สำหรับ Socket ที่เพิ่ม Event Listeners ++
    useEffect(() => {
        SocketManager.initializeSocket();
        
        const handleReceiveNotification = (data) => {
          console.log("Notification Socket event:", data);
          let myNoti = true;
          let userIdSocket = data.userId;
          let bankSocket = data.bankShortTo;
          let accountNumberSocket = data.accountNumberTo;

          let userId = subUser.userId;
          let bank = subUser.bank;
          let accountNumber = subUser.accountNumber;

          if(userIdSocket === userId){
            if(bank != null){
              if(bank == bankSocket){
                if(accountNumber != null){
                    if(accountNumber != accountNumberSocket){
                      myNoti = false
                    }
                }
              }else{
                myNoti = false
              }
            }

            if(myNoti){
              const formattedAmount = Number(data.amount).toFixed(2);
              setLatestAmount(formattedAmount);
              fetchNotifications();

              const wasScannedFromQR = getStatusScan();

              if(wasScannedFromQR){
                const onConfirm = () => {
                  setIsConfirmOpen(false);
                  saveStatusScan(false);
                };
                showConfirmation(
                    "ยืนยันรายการโอนเงินสำเร็จ ตรวจสอบจำนวนเงิน",
                    onConfirm,
                    false,
                    null,
                    false    
                );
              }


          
              // ทำงานตามโหมดที่ตั้งไว้
              if ((currentModeIndex === 0 || currentModeIndex === 1) && formattedAmount !== "0" && formattedAmount !== "0.00") {
                  let isSpeak = true;
                  if(currentModeIndex === 0 && !wasScannedFromQR){
                    isSpeak = false;
                  }
                  
                  if(isSpeak){
                    // จัดการ String สำหรับพูดให้เป็นธรรมชาติ (ตัด .00 ออก)
                    let amountToSpeak = formattedAmount;
                    if (amountToSpeak.endsWith('.00')) {
                        amountToSpeak = amountToSpeak.substring(0, amountToSpeak.length - 3);
                    }
            
                    // สั่งพูดครั้งที่ 1 
                    const firstUtterance = new SpeechSynthesisUtterance(`ชำระเงิน จำนวน ${amountToSpeak} บาท`);
                    firstUtterance.lang = 'th-TH';
                    const thaiVoice = voices.find(voice => voice.lang === 'th-TH');
                    if (thaiVoice) {
                        firstUtterance.voice = thaiVoice;
                    }
                    if (isTtsInitialized && ttsRef.current) {
                        ttsRef.current.speak(firstUtterance);
                    }
            
                    // สั่งพูดครั้งที่ 2 หลังจาก 2 วินาที 
                    setTimeout(() => {
                        const secondUtterance = new SpeechSynthesisUtterance(`${amountToSpeak} บาท`);
                        secondUtterance.lang = 'th-TH';
                        if (thaiVoice) {
                            secondUtterance.voice = thaiVoice;
                        }
                        if (isTtsInitialized && ttsRef.current) {
                            ttsRef.current.speak(secondUtterance);
                        }
                    }, 2000); 
                  }
                  
              } else if (currentModeIndex === 2 || currentModeIndex === 3) {
                let isSpeak = true;
                if(currentModeIndex === 2 && !wasScannedFromQR){
                  isSpeak = false;
                }

                if(isSpeak){
                  const audio = new Audio('/sounds/sound_bell.mp3');
                  audio.play().catch(err => console.error("Sound error:", err));
                }
              }
            }
            
          }
        };
        
      
        // ++ Event สำหรับบังคับ Logout เมื่อมีการ Login ซ้อน
        const handleSubUserLogin = (data) => {
            if (data.subUserId === subUser.id) {
                console.log("Duplicate login detected. Kicking user.");
                showKickLogoutDialog();
            }
        };

        SocketManager.addEventListener('receiveNotification', handleReceiveNotification);
        SocketManager.addEventListener('userStatus', handleUserStatus);
        SocketManager.addEventListener('subUserLogin', handleSubUserLogin);

        return () => {
            SocketManager.removeEventListener('receiveNotification', handleReceiveNotification);
            SocketManager.removeEventListener('userStatus', handleUserStatus);
            SocketManager.removeEventListener('subUserLogin', handleSubUserLogin);
        };
    }, [subUser, currentModeIndex, isTtsInitialized, voices]);

    const handleModeLeft = () => {
        const newMode = Math.max(0, currentModeIndex - 1);
        setCurrentModeIndex(newMode);
        localStorage.setItem('currentMode', newMode);
    };

    const handleModeRight = () => {
        const newMode = Math.min(modes.length - 1, currentModeIndex + 1);
        setCurrentModeIndex(newMode);
        localStorage.setItem('currentMode', newMode);
    };

  
    useEffect(() => {
      // ตรวจสอบก่อนเป็นอันดับแรกว่า browser นี้รองรับ Web Speech API หรือไม่
      if ('speechSynthesis' in window && window.speechSynthesis) {
        // ถ้า browser รองรับ, ให้ตั้งค่าสถานะและเริ่มกระบวนการ
        setIsTtsSupported(true);
        console.log("TTS is SUPPORTED in this browser. Initializing...");
    
        const tts = window.speechSynthesis;
        ttsRef.current = tts;
    
        const initTts = () => {
          // getVoices() อาจจะคืนค่า array ว่างในตอนแรก
          const availableVoices = tts.getVoices();
          if (availableVoices.length > 0) {
            setVoices(availableVoices);
            setIsTtsInitialized(true); // ตั้งค่าว่าพร้อมใช้งานเมื่อได้ voice list มาแล้ว
            console.log('TTS voices loaded successfully.');
            // เมื่อโหลดเสร็จแล้ว ไม่จำเป็นต้องฟัง event นี้อีกต่อไป
            tts.onvoiceschanged = null;
          }
        };
    
        // ผูก event listener เพื่อรอให้ voice list พร้อมใช้งาน
        tts.onvoiceschanged = initTts;
    
        // เรียกใช้ครั้งแรกด้วย เผื่อว่า voice โหลดมาแล้ว
        initTts();
    
      } else {
        // ถ้าไม่รองรับ ให้แสดง log และตั้งค่าสถานะให้ชัดเจน
        console.error("TTS is NOT SUPPORTED in this browser/WebView.");
        setIsTtsSupported(false);
      }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, []);

    // สำหรับนาฬิกาที่อัปเดตทุกวินาที
    useEffect(() => {
      const timerInterval = setInterval(() => setTime(new Date()), 1000);
      return () => clearInterval(timerInterval); // Cleanup function
    }, []);

    const openWebCheckSlip = () => {
      window.open('https://www.lmccheckslip.com', '_blank', 'noopener,noreferrer');
    };

    return (
        <>
        <div 
          className="relative w-full h-screen bg-dynamic-bg" 
          style={{ '--bg-color': bgColor, '--bd-color': bdColor }}
        >
          {/* ++ แสดงสถานะ Offline Indicator ++ */}
          {!isOnline && (
            <div className="absolute top-0 left-0 w-full bg-red-600 text-white text-center py-1 z-50">
              ไม่สามารถเชื่อมต่ออินเทอร์เน็ตได้
            </div>
          )}

          <div className="w-full h-full overflow-y-auto">
            <div className="p-4 bg-dynamic-bg flex flex-col">
              {/* Header Row */}
              <div className="flex flex-row items-center justify-between p-2">
                <span className="text-white text-lg cursor-pointer" onClick={logoutConfirm}>
                  {subUser.username?.toUpperCase()}
                </span>
                <img src="/images/left.png" alt="Left" className="w-6 h-6 cursor-pointer" onClick={handleModeLeft} />
                <div className="w-[35px] h-[35px] rounded-md shadow-lg bg-white flex items-center justify-center">
                  <img src={modes[currentModeIndex]} alt="Mode" className="w-5 h-5" />
                </div>
                <img src="/images/right.png" alt="Right" className="w-6 h-6 cursor-pointer" onClick={handleModeRight} />
                <span className="text-xl text-white">
                  {time.toLocaleTimeString('en-GB')}
                </span>
              </div>

              {/* Amount Card */}
              <div className="w-full h-[120px] bg-dynamic-bd rounded-lg shadow-md">
                <div className="relative w-[calc(100%-5px)] h-[calc(100%-5px)] mt-[5px] ml-[5px] bg-white rounded-lg flex flex-row items-center justify-center p-10 overflow-hidden">
                  <span className="absolute top-1 left-[5px] text-lg font-bold text-dynamic-bg z-10">
                    {bankShortTo === 'เป๋าตัง' ? bankTo : bankShortTo}
                  </span>
                  <div className="flex items-center justify-center w-full max-w-full">
                    <span className="text-black font-bold text-[60px] -my-5">{latestAmount.split('.')[0] || "0"}</span>
                    <span className="text-black font-bold text-[30px] mt-5 ml-1">.{latestAmount.split('.')[1] || "00"}</span>
                    <span className="text-black font-bold text-[30px] mt-4 ml-1">บาท</span>
                  </div>
                  <img
                    src={`/images/user_${(subUser.userStatus)}.png`}
                    alt="User Status"
                    className="absolute top-[5px] right-[5px] w-[18px] h-[18px] z-20"
                  />
                </div>
              </div>

              {/* Grid Layout for Detail & Notifications */}
              <div className="grid grid-cols-1 w-full mt-2" >
                {/* Detail Card */}
                <div className="w-full h-[80px] bg-dynamic-bd rounded-lg shadow-md" onClick={openWebCheckSlip}>
                  <div className="relative w-[calc(100%-5px)] h-[75px] ml-[5px] mt-[5px] rounded-lg bg-white flex flex-col justify-center shadow-lg">
                      {daysRemain !== null && (
                        <span 
                          className={`
                          absolute top-1 left-2 text-xs text-gray-600
                            ${daysRemain <= 3 ? 'animate-blink' : ''}
                          `}
                        >
                          &lt; {`${daysRemain}`} &gt;
                        </span>
                      )}
                    <div className="flex flex-col items-center">
                      <div className="flex w-full">
                      
                        <span className="flex-1 text-center text-lg text-black">{accountNumberTo}</span>
                      </div>
                      <div className="flex w-full">
                        <span className="flex-1 text-center text-lg text-black">{createdDate}</span>
                        <span className="flex-1 text-center text-lg text-black">{createdTime}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notification List Card */}
                <div className="overflow-x-auto whitespace-nowrap mt-2">
                    {loading ? (
                        <p>Loading notifications...</p>
                    ) : notifications.length > 0 ? (
                        <ResponsiveGroupedNotificationTable notifications={notifications} />
                    ) : (
                        <p>No notifications available.</p>
                    )}
                </div>
              </div>

            </div>
          </div>
          
          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              {/* --- ส่วนที่แก้ไข --- */}
              <div
                className="w-12 h-12 border-4 border-white border-t-blue-500 rounded-full animate-spin"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
              {/* --- สิ้นสุดส่วนที่แก้ไข --- */}
            </div>
          )}

          {/* Confirmation Modal */}
          <ConfirmModal
              isOpen={isConfirmOpen}
              message={countdown ? `${confirmMessage} (${countdown}s)` : confirmMessage}
              onConfirm={onConfirmCallback}
              onCancel={() => {
                clearInterval(countdownIntervalRef.current);
                setIsConfirmOpen(false);
              }}
              showCancelButton={showCancelButton}
          />
          
        </div>
        </>
    );
};

export default Notification;